import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Header, Input, Label, Message, Table } from 'semantic-ui-react';
import {
  FEET_TO_METER,
  METER_TO_FEET,
  SITE_MAX_HEIGHT_M,
} from 'src/app.constants';
import { encodeLatLong, isValidNumber } from 'src/utils/useful_functions';
import messages from '../../messages';
import { createSites, PAGE_SIZE } from './CSVSiteValidation';
import { getNearbySites } from './siteCsvImportUtils';
import { DESCRIPTION_MAX_LENGTH, NAME_MAX_LENGTH } from 'src/app.constants';
import { RootStateOrAny } from 'src/store';
import type {
  ImportedKmlSite,
  SiteGroupKind,
  SiteImportSubmitState,
} from './siteImportModalTypes';

type ProcessedKmlSite = Omit<ImportedKmlSite, 'lat' | 'lng'> & {
  lat: number;
  lng: number;
  nearbySite: boolean;
  truncatedColumns: Array<'description' | 'name'>;
};

type KMLSiteDataProps = {
  closeFunc: () => void;
  data: ImportedKmlSite[];
  groupKind?: SiteGroupKind;
  onSubmitStateChange: (state: SiteImportSubmitState | null) => void;
} & WrappedComponentProps;

function KMLSiteData(props: KMLSiteDataProps) {
  const {
    data,
    closeFunc,
    groupKind = 'network_sites',
    onSubmitStateChange,
    intl,
  } = props;
  const { formatMessage } = intl;
  const [isLoading, setIsLoading] = useState(false);
  const { projectId, prefs } = useSelector(
    (state: RootStateOrAny) => state.mainFrame
  );
  const sites = useSelector((state) => {
    if (groupKind === 'network_sites')
      return state['mainFrame']['networkSites'];
    else if (groupKind === 'subscriber_sites')
      return state['mainFrame']['subscriberSites'];
  });
  const { heightUnits } = prefs;
  const dispatch = useDispatch();
  const [height, setHeight] = useState(
    heightUnits === 'm' ? 10.0 : (10 * METER_TO_FEET).toFixed(1)
  );
  const headers = ['Name', 'Latitude', 'Longitude', 'Description'];

  const processedData = useMemo<ProcessedKmlSite[]>(() => {
    return data.map((row) => {
      const truncatedColumns: Array<'description' | 'name'> = [];
      const normalizedName =
        row.name.length > NAME_MAX_LENGTH
          ? row.name.substring(0, NAME_MAX_LENGTH)
          : row.name;
      const normalizedDescription =
        row.description && row.description.length > DESCRIPTION_MAX_LENGTH
          ? row.description.substring(0, DESCRIPTION_MAX_LENGTH)
          : row.description;

      if (normalizedName !== row.name) {
        truncatedColumns.push('name');
      }
      if (normalizedDescription !== row.description) {
        truncatedColumns.push('description');
      }

      const lat = Number(row.lat);
      const lng = Number(row.lng);

      return {
        ...row,
        lat,
        lng,
        description: normalizedDescription,
        name: normalizedName,
        nearbySite: getNearbySites(sites?.features ?? [], [lng, lat]),
        truncatedColumns,
      };
    });
  }, [data, sites]);

  const nearbySites = useMemo(
    () => processedData.filter((row) => row.nearbySite),
    [processedData]
  );
  const truncatedSites = useMemo(
    () => processedData.filter((row) => row.truncatedColumns.length > 0),
    [processedData]
  );
  const tableRows = useMemo(
    () =>
      (nearbySites.length > 0 ? nearbySites : processedData).slice(0, PAGE_SIZE),
    [nearbySites, processedData]
  );
  const canCreateSites = processedData.length > 0 && !isLoading;

  const handleCreateSites = useCallback(async () => {
    setIsLoading(true);
    const is_network_site = groupKind === 'network_sites';
    const maxHeight =
      heightUnits === 'm' ? height : Number(height) * FEET_TO_METER;
    const postData = processedData.map((obj) => {
      const { name, lat, lng, description } = obj;
      return {
        name,
        latitude: lat,
        longitude: lng,
        maximum_height: maxHeight,
        height_units: 'm',
        description,
        is_network_site,
      };
    });
    try {
      await createSites(
        dispatch,
        projectId,
        [],
        groupKind,
        closeFunc,
        'm',
        postData
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    closeFunc,
    dispatch,
    groupKind,
    height,
    heightUnits,
    processedData,
    projectId,
  ]);

  useEffect(() => {
    onSubmitStateChange({
      canSubmit: canCreateSites,
      isSubmitting: isLoading,
      onSubmit: handleCreateSites,
    });
  }, [canCreateSites, handleCreateSites, isLoading, onSubmitStateChange]);

  useEffect(
    () => () => {
      onSubmitStateChange(null);
    },
    [onSubmitStateChange]
  );

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Header as="h5" style={{ display: 'inline', paddingRight: '5px' }}>
            <FormattedMessage
              id="siteProperties.maxHeight"
              defaultMessage="Maximum Height"
            />
          </Header>
          <Input
            placeholder="Maximum height"
            type="number"
            step="0.01"
            onChange={(e, { value }) => {
              const maxValue =
                heightUnits === 'm'
                  ? SITE_MAX_HEIGHT_M
                  : SITE_MAX_HEIGHT_M * METER_TO_FEET;
              if (isValidNumber(value, 0, maxValue, 1)) {
                setHeight(value);
              }
            }}
            value={height}
          />
          <Label basic className="no-border" style={{ fontSize: '10px' }}>
            {heightUnits}
          </Label>
        </div>
        {nearbySites.length > 0 && (
          <>
            <div className="custom-warning">
              {formatMessage(messages.nearbySiteMsg)}
            </div>
            <Label className="custom-warning">
              {formatMessage(messages.warningSites)}: {nearbySites.length}
            </Label>
          </>
        )}
        {truncatedSites.length > 0 && (
          <>
            <div className="custom-warning">
              {formatMessage(messages.warningTruncate)}
            </div>
            <Label className="custom-warning">
              {formatMessage(messages.warningSites)}: {truncatedSites.length}
            </Label>
          </>
        )}
        <Label>
          {formatMessage(messages.totalSites)}: {processedData.length}
        </Label>
      </div>
      <div className="site-import-table">
        <Table celled compact>
          <Table.Header>
            <Table.Row>
              {headers.map((value: string) => {
                return <Table.HeaderCell key={value}>{value}</Table.HeaderCell>;
              })}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tableRows.map((row) => {
              return (
                <Table.Row
                  key={`${row.lat}-${row.lng}-${row.name}`}
                  className={row.nearbySite ? 'custom-warning' : ''}
                >
                  <Table.Cell
                    className={
                      row.truncatedColumns.includes('name')
                        ? 'custom-warning'
                        : 'kml-site-name'
                    }
                  >
                    {row.name}
                  </Table.Cell>
                  <Table.Cell>
                    {encodeLatLong(row.lat, prefs.latLngFormat)}
                  </Table.Cell>
                  <Table.Cell>
                    {encodeLatLong(row.lng, prefs.latLngFormat, false)}
                  </Table.Cell>
                  <Table.Cell style={{ maxWidth: '40vw' }}>
                    <div
                      className={
                        row.truncatedColumns.includes('description')
                          ? 'custom-warning'
                          : 'kml-site-description'
                      }
                    >
                      {row.description}
                    </div>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
      {processedData.length === 0 && (
        <Message attached="bottom" error>
          <FormattedMessage
            id="common.fileError"
            defaultMessage="Problem with file. Please send the file to linkplanner@cambiumnetworks.com"
          />
        </Message>
      )}
    </>
  );
}

export default injectIntl(KMLSiteData);
